import { useStore } from 'vuex'
import { ref, watch, computed } from 'vue'
import { clone, find, findIndex } from 'lodash'
import { useCart } from '@/app/Checkout/composables/useCart'
import { useUser } from '@/app/Checkout/composables/useUser'
import { useCheckout } from '@/app/Checkout/composables/useCheckout'
import { useAuth } from '@/app/Checkout/composables/useAuth'

const initialized = ref(false)

export function useAddresses() {
  const store = useStore()
  const { cart } = useCart()
  const { pending, addresses } = useUser()
  const { isLoggedIn } = useAuth()

  // Track the reactive store state via arrow function to ensure deep reactivity
  const errors = computed(() => store.state.user.errors)
  const pendingAddress = computed(() => store.state.user.pending)
  const countries = computed(() => store.state.country.countries)
  const countryOptions = computed(() => store.state.country.countries)
  const requiredFields = computed(() => cart.value?.required_fields || [])
  const { checkout, getCheckout, setInvoiceAddress } = useCheckout()

  const countryNameByCode = computed(() => {
    return Object.fromEntries(
      (countries.value || []).map(country => [country.value, country.label])
    )
  })

  const enrichedAddresses = computed(() => {
    if (!addresses.value?.length) return []

    return addresses.value.map(address => ({
      ...address,
      countryName: countryNameByCode.value[address.country] || address.country,
    }))
  })

  const isBillingAddressSame = computed({
    get: () => store.state.checkout.isBillingAddressSame,
    set: async val => {
      if (val === store.state.checkout.isBillingAddressSame) return

      store.commit('checkout/SET_IS_BILLING_ADDRESS_SAME', val)

      if (val === true) {
        await makeSameAddress()
      }

      const mappedId = mapAddressID()

      if (mappedId) {
        await setInvoiceAddress(mappedId)
        await getCheckout()
      }
    },
  })

  const getAddressString = type => {
    if (!checkout.value || !checkout.value[type]) {
      return ''
    }
    const checkAddress = addresses.value?.length ? addresses.value[0] : {}
    const checkFilled = Boolean(checkAddress.gender || checkAddress.firstname || checkAddress.lastname)
    const defaultAddress = addresses.value?.length === 1 && checkFilled ? addresses.value[0] : checkout.value[type]
    const option = defaultAddress.station_deliver || 'address'

    const firstText = `${defaultAddress.firstname || ''} ${defaultAddress.lastname || ''}`.trim()

    const preText = [
      defaultAddress.company,
      defaultAddress.address_details,
    ].filter(Boolean).join(', ')

    const middlePart =
      option === 'address'
        ? `${defaultAddress.address || ''} ${defaultAddress.house_number || ''}`.trim()
        : `${defaultAddress.postnumber || ''} ${defaultAddress.packstation || ''}`.trim()

    const postText = `${defaultAddress?.zip || ''} ${defaultAddress?.city || ''}`.trim()

    const lastText = countryOptions.value.find(
      option => option.value === defaultAddress?.country,
    )?.label

    const parts = [
      firstText,
      preText,
      middlePart,
      postText,
      lastText,
    ]

    return parts.filter(Boolean).join(', ')
  }

  const addressParamIsInvalid = type => {
    if (!checkout.value || !checkout.value[type]) {
      return false
    }
    const defaultAddress = addresses?.value.length === 1 ? addresses.value[0] : checkout.value[type]

    return defaultAddress.is_invalid
  }

  const isAddressValid = (address = {}) => {
    const addressType = address.station_deliver || 'address'

    return addressType === 'address'
      ? Boolean(
        address.address &&
        address.house_number &&
        address.zip &&
        address.city &&
        address.country,
      )
      : Boolean(
        address.postnumber &&
        address.packstation &&
        address.zip &&
        address.city,
      )
  }

  const isUserAddress = (
    type,
    checkoutData,
    addressesData,
  ) => {
    const checkoutObject = addresses.value?.length === 1 ? addresses.value[0] : checkoutData || checkout.value

    if (!checkoutObject || !checkoutObject[type]) {
      return true
    }
    const addressesObject = addressesData || addresses.value
    let noIdAddresses = clone(addressesObject).map(
      ({ id, ...item }) => item,
    )
    noIdAddresses = noIdAddresses.map(({ is_default, ...item }) => item)
    const usedAddress = clone(checkoutObject[type])
    delete usedAddress.id
    delete usedAddress.is_default

    return !!find(noIdAddresses, usedAddress)
  }

  const mapAddressID = (addressType = 'delivery_address') => {
    try {
      let noIdAddresses = clone(addresses.value).map(
        ({ id, ...item }) => item,
      )
      noIdAddresses = noIdAddresses.map(({ is_default, ...item }) => item)
      const usedAddress = clone(checkout.value[addressType])
      delete usedAddress.id
      delete usedAddress.is_default
      const index = findIndex(noIdAddresses, usedAddress)
      if (index !== -1) {
        return addresses.value[index].id
      }
      return null
    } catch (error) {
      return null
    }
  }

  const isDeliveryAddressFilled = (addressType = 'delivery_address') => {
    if (isLoggedIn.value) {
      return Boolean(mapAddressID(addressType))
    }
    if (checkout.value?.[addressType]) {
      const address = checkout.value[addressType]
      return Boolean(address.gender || address.firstname || address.lastname)
    }
    return false
  }

  const makeSameAddress = async () => {
    if (
      getAddressString('invoice_address') !== getAddressString('delivery_address')
    ) {
      const mappedId = mapAddressID()

      if (mappedId) {
        await setInvoiceAddress(mappedId)
        await getCheckout()
      } else {
        const usedAddress = clone(checkout.value.delivery_address)
        delete usedAddress.id
        usedAddress.is_invoice = true
        await createAddress(usedAddress)
        await getCheckout()
      }
    }
  }

  const getAddressFromId = addressId => enrichedAddresses.value.find(({ id }) => id == addressId)
  const getAddresses = () => store.dispatch('user/getAddresses')
  const resetAllErrors = () => store.commit('user/RESET_ALL_ERRORS')
  const setErrors = payload => store.commit('user/SET_ERRORS', payload)
  const resetErrors = payload => store.commit('user/RESET_ERRORS', payload)
  const createAddress = payload => store.dispatch('user/createAddress', payload)
  const updateAddress = payload => store.dispatch('user/updateAddress', payload)
  const deleteAddress = payload => store.dispatch('user/deleteAddress', payload)
  const clearFieldError = payload => store.commit('user/CLEAR_FIELD_ERROR', payload)

  watch(
    () => checkout.value,
    val => {
      if (!val || initialized.value) return

      store.commit('checkout/SET_IS_BILLING_ADDRESS_SAME', getAddressString('invoice_address') === getAddressString('delivery_address'))

      initialized.value = true
    },
    { immediate: true },
  )

  return {
    errors,
    pending,
    setErrors,
    countries,
    resetErrors,
    mapAddressID,
    getAddresses,
    isUserAddress,
    deleteAddress,
    createAddress,
    updateAddress,
    resetAllErrors,
    requiredFields,
    pendingAddress,
    isAddressValid,
    clearFieldError,
    makeSameAddress,
    getAddressString,
    getAddressFromId,
    enrichedAddresses,
    addressParamIsInvalid,
    isBillingAddressSame,
    isDeliveryAddressFilled,
  }
}
